import { AxiosError, AxiosResponse } from "axios";
import { toast } from "react-toastify";
import { push } from "connected-react-router";
import { put, takeLatest, takeLeading } from "redux-saga/effects";
import { Urls } from "src/constants";
import { HttpService, _ } from "src/services";
import { ModalsAction, PackagesAction, ProgramViewAction } from "../actions";
import { ReduxAction } from "../models";
import { PackagesType } from "../types";

export function* PackagesSaga() {
  yield takeLeading(PackagesType.REQUEST_CREATE_ORDER, createOrder);
  yield takeLatest(PackagesType.REQUEST_ORDER_INFO, getOrder);
  yield takeLeading(PackagesType.ADD_DISCOUNT_CODE, addDiscount);
  yield takeLeading(PackagesType.REMOVE_DISCOUNT_CODE, removeDiscount);
  yield takeLeading(PackagesType.PAY_ORDER, pay);
  yield takeLeading(PackagesType.SEND_VAS_SMS, sendSms);
  yield takeLeading(PackagesType.VERIFY_VAS_SMS, verifyVas);
  yield takeLeading(PackagesType.SEND_GIFT_CODE, sendGiftCode);
  yield takeLeading(PackagesType.REQUEST_PACKAGES, getPackages);
  yield takeLeading(PackagesType.REQUEST_PACKAGE_DETAIL, getPackageDetail);
}

function* getPackageDetail(action: ReduxAction) {
  try {
    const api = Urls.order.packageDetail(action.payload);
    const response: AxiosResponse = yield HttpService.Fetch(api);
    yield put(PackagesAction.setPackageDetail(response.data));
  } catch (ex: any) {
    const error: AxiosError = ex;
    if (error?.response?.status !== 404) {
      toast.error(_("errorOccured"));
      return;
    }

    const notFound: any = { id: -1 };
    yield put(PackagesAction.setPackageDetail(notFound));
  }
}

function* getPackages(action: ReduxAction) {
  try {
    const clear: boolean = action.payload;
    if (clear) yield put(PackagesAction.clear());

    const api = Urls.order.packages();
    const response: AxiosResponse = yield HttpService.Fetch(api);
    yield put(PackagesAction.setPackages(response.data));
  } catch {
    yield put(PackagesAction.setPackages([]));
  }
}

function* sendGiftCode(action: ReduxAction) {
  try {
    const code = action.payload;
    const api = Urls.profile.giftCode(code);
    const response: AxiosResponse = yield HttpService.Fetch(api);
    yield put(PackagesAction.giftCodeSent(true));
    toast.success(response.data.message);
    yield put(ModalsAction.hideGift());
  } catch {
    yield put(PackagesAction.giftCodeSent(false));
  }
}

function* sendSms(action: ReduxAction) {
  try {
    const url = action.payload;

    yield HttpService.Fetch(Urls.packApi("", url, "POST", false), {});
    yield put(PackagesAction.SmsSent());
  } catch {
    toast.error(_("errorOccured"));
    yield put(PackagesAction.hideLoading());
  }
}

function* verifyVas(action: ReduxAction) {
  try {
    const { url, code, programId } = action.payload;

    yield HttpService.Fetch(Urls.packApi("", url, "POST", false), { code });
    yield put(ProgramViewAction.requestProgramInfo(programId));
    yield put(PackagesAction.hideLoading());
    yield put(ModalsAction.hidePackages());
  } catch {
    toast.error(_("errorOccured"));
    yield put(PackagesAction.hideLoading());
  }
}

function* createOrder(action: ReduxAction) {
  try {
    const model = action.payload;
    const api = Urls.order.create();
    const response: AxiosResponse = yield HttpService.Fetch(api, model);

    if (response.data) {
      try {
        document.getElementsByTagName("body")[0].classList.remove("modal-open");
      } catch {}

      yield put(push(`/payment/${response.data.id}`));
      yield put(PackagesAction.hideLoading());
      yield put(ModalsAction.hidePackages());
    }
  } catch (error: any) {
    const axiosError: AxiosError = error;

    if (!axiosError.response || !axiosError.response.data) {
      toast.error(_("errorOccured"));
    }
    yield put(PackagesAction.hideLoading());
  }
}

function* getOrder(action: ReduxAction) {
  try {
    const api = Urls.order.info(action.payload);
    const response: AxiosResponse = yield HttpService.Fetch(api);

    if (response.data) {
      yield put(PackagesAction.setOrderInfo(response.data));
    }
  } catch (ex: any) {
    const error: AxiosError = ex;
    if (error?.response?.status !== 404) {
      toast.error(_("errorOccured"));
      return;
    }

    const notFound: any = { id: -1 };
    yield put(PackagesAction.setOrderInfo(notFound));
  }
}

function* addDiscount(action: ReduxAction) {
  try {
    const model = action.payload;
    const api = Urls.order.addDiscount();
    const response: AxiosResponse = yield HttpService.Fetch(api, model);

    if (response.data) {
      yield put(PackagesAction.hideLoading());
      yield put(PackagesAction.setOrderInfo(response.data));
    }
  } catch (ex: any) {
    yield put(PackagesAction.hideLoading());

    const error: AxiosError = ex;
    if (error.response && error.response.status === 404) {
      toast.error(_("invalidDiscountCode"));
      return;
    }
    toast.error(_("errorOccured"));
  }
}

function* removeDiscount(action: ReduxAction) {
  try {
    const model = action.payload;
    const api = Urls.order.removeDiscount();
    const response: AxiosResponse = yield HttpService.Fetch(api, model);

    if (response.data) {
      yield put(PackagesAction.hideLoading());
      yield put(PackagesAction.setOrderInfo(response.data));
    }
  } catch (ex: any) {
    toast.error(_("errorOccured"));
    yield put(PackagesAction.hideLoading());
  }
}

function* pay(action: ReduxAction) {
  try {
    const { model, payUrl } = action.payload;
    const response: AxiosResponse = yield HttpService.Fetch(
      Urls.packApi(null, payUrl, "POST", false),
      model
    );

    if (response.data.url) {
      yield put(PackagesAction.hideLoading());
      window.location = response.data.url;
    }
  } catch (ex: any) {
    toast.error(_("errorOccured"));
    yield put(PackagesAction.hideLoading());
  }
}
